import Cast from './Cast';
import DeviceType from './DeviceType';

// Oeffnet eine Adresse im EIGENEN Browser des Fernsehers statt im iframe der App.
//
// Warum es das braucht: eine eingebettete Fremdseite laeuft auf diesen Geraeten
// im selben Renderer-Prozess und damit auf demselben Main-Thread wie die TV-App
// (Site-Isolation ist in den TV-Firmwares meist abgeschaltet). Eine schwere
// Seite legt damit die ganze App still - die Fernbedienung reagiert nicht mehr,
// weil unser eigenes JavaScript nicht mehr laeuft. Im Browser des TV laeuft die
// Seite in einem eigenen Prozess, und der Browser bringt seinen eigenen Ausstieg
// mit.
//
// Die konkreten Aufrufe sind auf beiden Plattformen NICHT verlaesslich
// dokumentiert und weichen je Firmware ab; auf Hospitality-Geraeten ist der
// Browser haeufig gar nicht installiert. Deshalb wird der Reihe nach probiert,
// jeder Schritt geloggt, und im Misserfolgsfall rejected das Promise mit einem
// lesbaren Grund - der Aufrufer zeigt ihn an, statt still nichts zu tun.

// Woran eine Browser-App in der App-Liste des Geraets erkannt wird. Gesucht wird
// in Id UND Name, kleingeschrieben.
const BROWSER_HINTS = ['browser', 'internet'];

// Bekannte App-Id des WebOS-Browsers. Auf LG ist sie ueber die Generationen
// stabil; auf Samsung gibt es keine vergleichbar verlaessliche, dort laeuft der
// erste Versuch deshalb ohne Id (siehe openTizen).
const LG_BROWSER_ID = 'com.webos.app.browser';

function log(msg) {
  console.log('[Browser] ' + msg);
}

function errText(e) {
  if (!e) return 'unbekannt';
  return String(e.errorMessage || e.message || e.name || e);
}

// Sucht in einer Liste von Apps die, die nach einem Browser aussieht.
// toText liefert den durchsuchbaren Text eines Eintrags.
function pickBrowser(list, toText) {
  const items = list || [];
  for (let i = 0; i < BROWSER_HINTS.length; i++) {
    const hint = BROWSER_HINTS[i];
    for (let j = 0; j < items.length; j++) {
      const text = String(toText(items[j]) || '').toLowerCase();
      if (text.indexOf(hint) !== -1) return items[j];
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Samsung Tizen
// ---------------------------------------------------------------------------

// Erster und bester Versuch: den Standard-Viewer fuer eine http-Adresse starten.
// Das ist der dokumentierte Weg (ApplicationControl mit der Operation "view"),
// er braucht KEINE App-Id - welche App zustaendig ist, entscheidet das Geraet -
// und er ist der einzige, der die Adresse sicher mit uebergibt. Das Privileg
// application.launch steht in der config.xml der WGT bereits.
function tizenAppControl(url) {
  return new Promise((resolve, reject) => {
    const t = window.tizen;
    if (!t || !t.application || typeof t.application.launchAppControl !== 'function') {
      reject(new Error('tizen.application.launchAppControl fehlt'));
      return;
    }
    try {
      const control = new t.ApplicationControl(
        'http://tizen.org/appcontrol/operation/view',
        url,
        null,
        null,
        null
      );
      t.application.launchAppControl(
        control,
        null,
        function() {
          log('launchAppControl(view) gestartet');
          resolve('appcontrol');
        },
        function(e) {
          log('launchAppControl(view) fehlgeschlagen: ' + errText(e));
          reject(e || new Error('launchAppControl fehlgeschlagen'));
        },
        null
      );
    } catch (e) {
      log('launchAppControl(view) wirft: ' + errText(e));
      reject(e);
    }
  });
}

// Zweiter Versuch: die installierten Apps aufzaehlen und die Browser-App per WAS
// mit der Adresse als Metadatum starten. Aufzaehlen statt raten - die Ids weichen
// je Firmware und Region ab, und was hier nicht auftaucht, ist auch nicht
// installiert. Das Privileg application.info steht in der config.xml.
function tizenLaunchBrowserApp(url) {
  return new Promise((resolve, reject) => {
    const t = window.tizen;
    if (!t || !t.application || typeof t.application.getAppsInfo !== 'function') {
      reject(new Error('tizen.application.getAppsInfo fehlt'));
      return;
    }
    try {
      t.application.getAppsInfo(
        function(apps) {
          const hit = pickBrowser(apps, function(a) {
            return (a.id || '') + ' ' + (a.name || '');
          });
          if (!hit) {
            const names = (apps || [])
              .map(function(a) {
                return (a.name || '?') + ' [' + a.id + ']';
              })
              .join(', ');
            log('keine Browser-App installiert. Vorhandene Apps: ' + names);
            reject(new Error('keine Browser-App auf diesem Geraet'));
            return;
          }
          log('Browser-App gefunden: ' + (hit.name || '?') + ' [' + hit.id + ']');
          // metaData traegt die Adresse. Ob die Browser-App sie auswertet, ist
          // nicht dokumentiert. tizen.application.launch koennte sie gar nicht
          // uebergeben und wuerde nur die Startseite oeffnen - das ist nicht,
          // was der Gast angetippt hat, deshalb steht es nicht als weiterer
          // Notnagel dahinter.
          Cast.launchApp(hit.id, { metaData: url, name: hit.name })
            .then(function() {
              resolve(hit.id);
            })
            .catch(reject);
        },
        function(e) {
          log('getAppsInfo fehlgeschlagen: ' + errText(e));
          reject(e || new Error('getAppsInfo fehlgeschlagen'));
        }
      );
    } catch (e) {
      reject(e);
    }
  });
}

function openTizen(url) {
  return tizenAppControl(url).catch(function(first) {
    return tizenLaunchBrowserApp(url).catch(function(second) {
      return Promise.reject(
        new Error(
          'Browser nicht startbar (AppControl: ' +
            errText(first) +
            '; App-Liste: ' +
            errText(second) +
            ')'
        )
      );
    });
  });
}

// ---------------------------------------------------------------------------
// LG WebOS
// ---------------------------------------------------------------------------

// Der WebOS-Browser nimmt die Zieladresse als Startparameter (target). Der
// Cast-Helfer bringt das Ausweichen auf die Preloaded-Liste und das Nachziehen
// von asl_mode schon mit, deshalb laeuft der Start hier ueber ihn.
function openLG(url) {
  return Cast.launchApp(LG_BROWSER_ID, {
    parameters: { target: url },
    name: 'browser',
  }).catch(function(e) {
    log('launchApplication(' + LG_BROWSER_ID + ') fehlgeschlagen: ' + errText(e));
    return Promise.reject(new Error('Browser nicht startbar (' + errText(e) + ')'));
  });
}

export default class Browser {
  // Kann dieses Geraet ueberhaupt einen eigenen Browser starten? Bewusst
  // grosszuegig: ob die App wirklich installiert ist, zeigt erst der Versuch -
  // und der meldet dann einen lesbaren Grund.
  static get isSupported() {
    if (DeviceType.isTizen) return !!(window.tizen && window.tizen.application);
    if (DeviceType.isLG) return typeof hcap !== 'undefined' && !!hcap;
    if (DeviceType.isBrowser) return true;
    return false;
  }

  // Oeffnet url im Browser des Geraets. Resolved mit einer Kennung des Weges,
  // der funktioniert hat; rejected mit einem anzeigbaren Grund.
  static open(url) {
    const target = String(url || '').trim();
    if (!target) return Promise.reject(new Error('keine Adresse'));
    log('oeffne im Geraete-Browser: ' + target);
    if (DeviceType.isTizen) return openTizen(target);
    if (DeviceType.isLG) return openLG(target);
    // Entwicklung im Desktop-Browser: neues Fenster, damit der Ablauf ueberhaupt
    // testbar ist.
    if (DeviceType.isBrowser) {
      const w = window.open(target, '_blank');
      return w ? Promise.resolve('window.open') : Promise.reject(new Error('Fenster wurde blockiert'));
    }
    return Promise.reject(new Error('auf dieser Plattform nicht moeglich'));
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Browser.js